import { useMemo } from "react";

import {
  type GetJsInterviewPrepResumeImprovementsQuery,
  useGetJsInterviewPrepResumeImprovementsQuery,
} from "../graphql/__generated__/GetJsInterviewPrepResumeImprovements.gqlgenerated";

/**
 * The improvements payload returned by interview-prep-service for a single
 * extraction. Non-null only once the pipeline `status` is COMPLETE.
 */
export type JsInterviewPrepResumeImprovementsPayload = NonNullable<
  GetJsInterviewPrepResumeImprovementsQuery["getJsInterviewPrepResumeImprovements"]
>;

/** The section-grouped resume improvement tree (work experience, skills, etc.). */
export type JsInterviewPrepResumeImprovementsTree = NonNullable<
  JsInterviewPrepResumeImprovementsPayload["resume"]
>;

/**
 * Pipeline status for an extraction.
 * - `PROCESSING` — still running; `resume`, `extractedAt`, `pendingImprovementCount` are null. Show loading.
 * - `COMPLETE` — improvements available; render the resume tree.
 * - `FAILED` — no improvements could be made (missing data or resume already complete). Empty state, NOT an error.
 * - `ERROR` — genuine technical failure; show an error state.
 */
export type JsInterviewPrepResumeImprovementsStatus =
  JsInterviewPrepResumeImprovementsPayload["status"];

export interface UseJsInterviewPrepResumeImprovementsParams {
  /**
   * The extraction (pipeline) id to fetch. Omit to fetch the latest extraction
   * for the authenticated jobseeker. The jobseeker is resolved from auth context;
   * there is no accountId argument.
   */
  extractionId?: string;
  /** When true, the query is not fired (e.g. the screen is not yet visible). */
  skip?: boolean;
  /** CTA that launched the flow (e.g. "viewjob", "summaryscreen"), recorded server-side for request attribution. */
  source?: string;
}

export interface UseJsInterviewPrepResumeImprovementsResult {
  /** True while the GraphQL request is in flight. */
  loading: boolean;
  /** A transport-level GraphQL/network error (distinct from a `status: ERROR` payload). */
  error?: Error;
  /** Pipeline status. Undefined until the first response resolves. */
  status?: JsInterviewPrepResumeImprovementsStatus;
  /** Number of improvements awaiting a jobseeker decision. Null/undefined until COMPLETE. */
  pendingImprovementCount?: number | null;
  /** ISO timestamp of when the improvements were extracted. Null/undefined until COMPLETE. */
  extractedAt?: string | null;
  /** The extraction id this payload belongs to. */
  extractionId?: string;
  /** The interview the improvements are based on. */
  interviewId?: string | null;
  /** The job used for extraction (may be null if no matching job was found). */
  jobKey?: string | null;
  /** The section-grouped improvement tree. Null/undefined until COMPLETE. */
  resume?: JsInterviewPrepResumeImprovementsTree | null;
  /** The full payload, for callers that need raw access. */
  payload?: JsInterviewPrepResumeImprovementsPayload | null;
  /**
   * Whether a "Review suggestions" CTA should render:
   * `status === 'COMPLETE' && pendingImprovementCount > 0`.
   */
  shouldShowCta: boolean;
  /** Re-runs the query against the server. */
  refetch: () => Promise<unknown>;
}

/**
 * Fetch hook / data provider for the Mock Interview Resume Improvements (IP-86)
 * Review flow. Wraps the generated `GetJsInterviewPrepResumeImprovements` query
 * and exposes the top-level fields the Review screen (RN-5564) and entry-point
 * CTAs need, plus a derived `shouldShowCta` gate.
 *
 * Modeled on the onboarding `ResumeGlowupDataProviderBridge`, swapping the data
 * source from `getResumeImprovements` to `getJsInterviewPrepResumeImprovements`.
 */
export const useJsInterviewPrepResumeImprovements = ({
  extractionId,
  skip = false,
  source,
}: UseJsInterviewPrepResumeImprovementsParams = {}): UseJsInterviewPrepResumeImprovementsResult => {
  const { data, loading, error, refetch } = useGetJsInterviewPrepResumeImprovementsQuery({
    // `extractionId` is optional; omitting it asks the server for the latest extraction.
    variables: {
      input: {
        ...(extractionId === undefined ? {} : { extractionId }),
        ...(source === undefined ? {} : { source }),
      },
    },
    skip,
  });

  const payload = data?.getJsInterviewPrepResumeImprovements;

  return useMemo(() => {
    const status = payload?.status;
    const pendingImprovementCount = payload?.pendingImprovementCount;
    const shouldShowCta = status === "COMPLETE" && (pendingImprovementCount ?? 0) > 0;

    return {
      loading,
      error,
      status,
      pendingImprovementCount,
      extractedAt: payload?.extractedAt,
      extractionId: payload?.extractionId,
      interviewId: payload?.interviewId,
      jobKey: payload?.jobKey,
      resume: payload?.resume,
      payload,
      shouldShowCta,
      refetch,
    };
  }, [payload, loading, error, refetch]);
};
